<template>
    <div v-if="props.dataset" class="flex flex-col h-full overflow-hidden overview-container">
        
        <!-- Main content -->
        <div class="flex-1 px-6 pb-4 overflow-hidden">
            <div class="grid grid-cols-12 gap-6 h-full overflow-hidden overview-grid">
                
                <!-- Resume -->
                <div class="col-span-12 lg:col-span-7 rounded border bg-gray-50 h-[55vh] min-h-[400px] flex-shrink-0 overview-column">
                    <div class="p-6 h-full flex flex-col justify-between overflow-hidden">
                        
                        <div class="px-3 overflow-y-auto flex-1">

                            <div class="text-gray-700 font-medium">{{ $t('Resume') }}</div>
                            <div v-if="props.dataset.resumeTxt" class="text-xs text-gray-600 mt-4">{{ props.dataset.resumeTxt?.resume }}</div>

                            <div class="text-gray-700 font-medium mt-8">{{ $t('A brief description of the goal') }}</div>
                            <div v-if="props.dataset.description" class="text-xs text-gray-600 mt-4">{{ props.dataset.description }}</div> 
                            <div v-else class="text-xs text-gray-600 mt-4">{{ $t('Not defined') }}.</div>

                        </div>

                        <div class="mt-5 flex justify-end flex-shrink-0">
                            <button type="button" class="rp-btn-action-secondary flex items-center space-x-1 w-auto" style="flex: none; font-size: small" @click="$emit('showEditDatasetModal')">
                                <Edit3Icon class="w-4 h-4" />
                                <span>{{ $t('Edit description') }}</span>
                            </button>
                        </div>

                    </div>
                </div>

                <!-- Status -->
                <div class="col-span-12 lg:col-span-5 rounded border bg-gray-50 h-[55vh] min-h-[400px] flex-shrink-0 overview-column">
                    
                    <div v-if="props.dataset?.tagStats" class="p-6 h-full overflow-y-auto">
                        
                        <div v-if="props.dataset?.datasetStatus" class="flex items-center border-b border-gray-200 pb-2 mb-6">
                            <div class="text-gray-700 font-medium">{{ $t('Status') }}</div>
                            <span class="text-green-700 ml-auto">{{ 'Ready' }}</span>
                        </div>
                        <div v-else>
                            <div class="intro-y col-span-12 lg:col-span-12 flex flex-col justify-center items-center pb-2 mb-6">
                                <div class="text-xs text-center">{{ $t('Analyzing the dataset') }}</div>
                                <LoadingIcon icon="three-dots" class="w-8 h-8" />
                            </div>
                        </div>

                        <div class="flex items-center border-b border-gray-200 pb-2 mb-6">
                            <div>
                                <div class="text-gray-600">{{ $t('Images') }}</div>
                                <div class="mt-1 text-xs font-medium rp-text-blue-1 cursor-pointer" @click="$emit('updateTab', 'upload')">{{ $t('manage') }}</div>
                            </div>
                            <span class="text-gray-600 ml-auto text-xl pb-2">
                                <span v-if="props.dataset.tagStats?.total">{{ $h.formatScore(props.dataset.tagStats.total) }}</span>
                                <span v-else>0</span>
                            </span>
                        </div>

                        <div class="flex items-center border-b border-gray-200 pb-2 mb-6">
                            <div>
                                <div class="text-gray-600">{{ $t('Tags') }}</div>
                                <div class="mt-1 text-xs font-medium rp-text-blue-1 cursor-pointer" @click="$emit('updateTab', 'tags')">{{ $t('manage') }}</div>
                            </div>
                            <span v-if="filteredTags && Object.keys(filteredTags).length" class="text-gray-600 ml-auto text-xl pb-2">
                                {{ Object.keys(filteredTags).length }}
                            </span>
                            <span v-else class="text-gray-600 ml-auto text-xl pb-2">0</span>
                        </div>

                        <div class="flex items-center">
                            <div>
                                <div class="text-gray-600">{{ $t('Models') }}</div>
                                <div class="mt-1 text-xs font-medium rp-text-blue-1 cursor-pointer" @click="$emit('updateTab', 'models')">{{ $t('manage') }}</div>
                            </div>
                            <span v-if="props.dataset?.models?.count" class="text-gray-600 ml-auto text-xl pb-2">{{ props.dataset.models.count }}</span>
                            <span v-else class="text-gray-600 ml-auto text-xl pb-2">0</span>
                        </div>

                    </div>

                    <div v-else>
                        <div class="intro-y col-span-12 lg:col-span-12 flex flex-col justify-center items-center p-10">
                            <div class="text-xs text-center">{{ $t('Analyzing the dataset') }}</div>
                            <LoadingIcon icon="three-dots" class="w-8 h-8" />
                        </div>
                    </div>

                </div>

            </div>
        </div>

        <!-- Image Carousel -->
        <div class="px-6 pt-2 pb-6 flex-shrink-0 overflow-hidden">
            <div v-if="props.dataset.id" class="w-full bg-gray-50 rounded border overflow-hidden">
                <ImageCarousel ref="imageCarouselRef"
                    :dataset="props.dataset"
                    @updateTab="$emit('updateTab', $event)"
                    @editImage="$emit('editImage', $event)"/>
            </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-3 border-t border-gray-200 text-xs text-gray-500 flex justify-between bg-white flex-shrink-0">
            <span v-if="props.dataset.updatedDate">{{ $t('Dataset updated on') }} {{ props.dataset.updatedDate }}</span>
            <span>{{ $t('Dataset') }} {{ props.dataset.idx + 1 }} {{ $t('of') }} {{ Object.keys(props.datasetsList).length }}</span>
        </div>

    </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { helper as $h } from "@/utils/helper";

import ImageCarousel from "@/components/manage-image/ImageCarousel.vue";

const imageCarouselRef = ref(null);

const filteredTags = computed(() => {
    if (props.dataset) {

        switch (props.dataset.type) {
            case 'MULTICLASS':
            case 'ANOMALY':
            case 'imageObjectDetection':
                return Object.entries(props.dataset.tags).reduce((acc, [id, tag]) => {
                    if (!tag?.unclassified) { acc[id] = tag; }
                    return acc;
                }, {});
            case 'MULTILABEL':
                return Object.entries(props.dataset.tags).reduce((acc, [id, tag]) => {
                    if (!tag?.anomaly && !tag?.normal) { acc[id] = tag; }
                    return acc;
                }, {});
            default:
                return props.dataset.tags || {};
        }
    }
    return {};
});

const props = defineProps({
    dataset: {
        type: Object,
        default: () => ({})
    },
    datasetsList: {
        type: Array,
        default: () => []
    },
});

watch(() => props.dataset.tagStats, async () => {
	imageCarouselRef?.value?.loadImages();
}, { deep: true });
</script>

<style>
#fixedMosaicBox{  
    border-radius: 8px;
    background-color: #fff; 
    margin-right: 15px;
}


.overview-container {
    max-width: 100vw;
    overflow-x: hidden;
}

@media (min-resolution: 2dppx) and (max-width: 1400px) {
    .overview-column {
        height: clamp(350px, 50vh, 450px) !important;
        min-height: 350px !important;
    }
}

@media (max-width: 1024px) {
    .overview-grid {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
    
    .overview-column {
        height: auto !important;
        min-height: 350px;
        max-height: 500px;
    }
}

@media (min-resolution: 1.8dppx) and (max-height: 800px) {
    .overview-column {
        height: 40vh !important;
        min-height: 300px !important;
    }
}
</style>